import { useEffect, useMemo } from "react";
import { Box, Grid, LinearProgress, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineMapPin } from "react-icons/hi2";
import FormDialog, { PlainCard } from "../../../../components/FormDialog";
import { closePosLocationsDialog } from "../../../../app/slices/posSlice";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import dayjs from "dayjs";
import "leaflet/dist/leaflet.css";

const DEFAULT_CENTER = [33.325891, 44.47519];

const formatUpdatedAt = (value) =>
  value ? dayjs(value).format("YYYY-MM-DD hh:mm a") : "-";

const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char],
  );

// Pin-shaped marker so the device locations stand out from the default icon
// used by the all-POS map.
//
// `pos_locations` are the devices of a single POS, so they sit metres apart and
// stay bunched together at every zoom level. Permanent Leaflet tooltips put all
// of their labels at the same spot, leaving only the topmost one readable, so
// each label is instead drawn inside its own marker icon, lifted a row clear of
// the one below it and joined back to its pin by a leader line. Ranking the
// rows by latitude (see `labelRows`) makes the stagger run the same way the
// pins do, so the rows spread apart rather than cancelling out.
const LABEL_ROW = 26;

const getMarkerIcon = (label, row) =>
  L.divIcon({
    html: `
    <div class="pos-location-marker" style="--label-lift:${row * LABEL_ROW}px">
      <span class="pos-location-leader"></span>
      <span class="pos-location-label">${escapeHtml(label)}</span>
      <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M15 0C9.477 0 5 4.477 5 10c0 7.5 10 20 10 20s10-12.5 10-20c0-5.523-4.477-10-10-10z"
          fill="#7b1fa2"
          stroke="#fff"
          stroke-width="2"
        />
        <circle cx="15" cy="10" r="4" fill="#fff" />
      </svg>
    </div>
  `,
    className: "",
    // The box is wide enough for the label; the pin tip sits at the bottom
    // centre, so that is where the anchor goes.
    iconSize: [200, 64],
    iconAnchor: [100, 64],
    popupAnchor: [0, -44],
  });

// Keeps every pin in view: a single location is centered, several are framed.
const FitBounds = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (positions.length === 1) {
      map.setView(positions[0], 16);
    } else if (positions.length > 1) {
      map.fitBounds(positions, { padding: [50, 50] });
    }
  }, [map, positions]);

  return null;
};

const PosLocationsMap = () => {
  const dispatch = useDispatch();

  const items = useSelector((state) => state.pointOfsales.posLocations);

  const locations = useMemo(
    () =>
      (items?.data || []).filter(
        (location) =>
          location.latitude !== null &&
          location.longitude !== null &&
          !isNaN(Number(location.latitude)) &&
          !isNaN(Number(location.longitude)),
      ),
    [items?.data],
  );

  const positions = useMemo(
    () =>
      locations.map((location) => [
        Number(location.latitude),
        Number(location.longitude),
      ]),
    [locations],
  );

  // Row index per marker: northern-most pin (drawn highest) gets the top row.
  const labelRows = useMemo(() => {
    const rank = new Map();
    positions
      .map((position, index) => ({ index, lat: position[0] }))
      .sort((a, b) => a.lat - b.lat)
      .forEach((entry, row) => rank.set(entry.index, row));
    return rank;
  }, [positions]);

  const handleDialog = () => {
    dispatch(closePosLocationsDialog());
  };

  return (
    <FormDialog
      open={items.dialog}
      onClose={() => {
        handleDialog();
      }}
      icon={<HiOutlineMapPin />}
      title="مواقع نقطة البيع"
      subtitle={[
        items?.pos?.name,
        !items.loading && `عدد المواقع: ${locations.length}`,
      ]
        .filter(Boolean)
        .join(" · ")}
      maxWidth="lg"
    >
      <PlainCard>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            {items.loading ? (
              <Box
                sx={{
                  height: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ width: "50%" }}>
                  <LinearProgress />
                </Box>
              </Box>
            ) : locations.length === 0 ? (
              <Box
                sx={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  لا توجد مواقع مسجلة لنقطة البيع
                </Typography>
              </Box>
            ) : (
              <MapContainer
                center={positions[0] ?? DEFAULT_CENTER}
                zoom={16}
                scrollWheelZoom={true}
                style={{ height: 800, width: "100%" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds positions={positions} />
                {locations.map((location, index) => (
                  <Marker
                    key={location.id ?? index}
                    position={positions[index]}
                    icon={getMarkerIcon(
                      formatUpdatedAt(location.updated_at),
                      labelRows.get(index) ?? 0,
                    )}
                  >
                    <Popup>
                      <Box sx={{ minWidth: 200 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {items?.pos?.name ?? "نقطة البيع"}
                        </Typography>
                        <Typography variant="body2">
                          الرقم التسلسلي: {location.serial_number ?? "-"}
                        </Typography>
                        <Typography variant="body2">
                          معرف الجهاز: {location.device_id ?? "-"}
                        </Typography>
                        <Typography variant="body2">
                          الإحداثيات: {location.latitude}, {location.longitude}
                        </Typography>
                        <Typography variant="body2">
                          اخر تحديث: {formatUpdatedAt(location.updated_at)}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="a"
                          href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          فتح في خرائط جوجل
                        </Typography>
                      </Box>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </Grid>
        </Grid>
      </PlainCard>
    </FormDialog>
  );
};

export default PosLocationsMap;
